'use client';

import type { ReactElement, ReactNode } from 'react';
import { Box } from '@terra/ui-styled-system';
import { Collapsible } from '../../../../components/collapsible';
import { useTraceOpen } from './use-trace-open';

type TraceCollapsibleProps = {
  open?: boolean;
  defaultOpen?: boolean;
  running?: boolean;
  onOpenChange?: (details: { open: boolean }) => void;
  disabled?: boolean;
  children: ReactNode;
};

export function TraceCollapsible({
  open,
  defaultOpen,
  running,
  onOpenChange,
  disabled,
  children,
}: TraceCollapsibleProps) {
  const traceOpen = useTraceOpen({
    open,
    running,
    initialUserOverride: defaultOpen,
    onUserChange: (nextOpen) => onOpenChange?.({ open: nextOpen }),
  });
  const isOpen = open ?? traceOpen.isOpen;

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={onOpenChange ?? traceOpen.onOpenChange}
      disabled={disabled}
    >
      <Box w="full" minW={0}>
        {children}
      </Box>
    </Collapsible.Root>
  );
}

export function TraceCollapsibleTrigger({
  children,
  disabled,
}: {
  children: ReactElement;
  disabled?: boolean;
}) {
  return (
    <Collapsible.Trigger asChild disabled={disabled}>
      {children}
    </Collapsible.Trigger>
  );
}

export function TraceCollapsibleContent({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  if (!open) {
    return null;
  }

  return (
    <Collapsible.Content style={{ transition: 'max-height 200ms ease-out' }}>
      {children}
    </Collapsible.Content>
  );
}
